import { parseContentUri } from "lib/actions/assets/parseContentUri";
import { AssetTypeAdapterInterface } from "./AssetTypeAdapterInterface";
import { RenderableAsset } from "lib/types/assets/RenderableAsset";
import { Guard } from "lib/actions/util";
import RasterLayerPlugin from "../RasterLayerPlugin";
import CameraPlugin from "components/elements/CesiumViewer/core/wrapper/CameraPlugin";
import {
  IonImageryProvider,
  UrlTemplateImageryProvider,
  ImageryLayer,
} from "cesium";

export class RasterLayerAssetTypeAdapter implements AssetTypeAdapterInterface {
  constructor(
    private readonly _rasterLayer: RasterLayerPlugin,
    private readonly _camera: CameraPlugin,
  ) {}

  isExisting(uri: string) {
    return this._rasterLayer.isExisting(uri);
  }

  isReady(uri: string) {
    return this._rasterLayer.isExisting(uri); //is immediately ready
  }

  isShowing(uri: string) {
    return this._rasterLayer.isShowing(uri);
  }

  async load(version: RenderableAsset) {
    Guard.require(version, "version");
    if (version.asset.type !== "rasterImagery") {
      throw new Error(
        `Unsupported asset type '${version.asset.type}', must be 'rasterImagery'.`,
      );
    }
    const uri = parseContentUri(version.contentUri);

    if (uri.location === "self") {
      if (!uri.value) {
        throw new Error(
          `Invalid MapTiler id. Value '${uri.value}' must be a valid string.`,
        );
      }
      await this.loadMapTiler(version, uri.value);
    } else if (uri.location === "ion") {
      const ionId = +uri.value;
      if (!ionId || Number.isNaN(ionId)) {
        throw new Error(
          `Invalid Cesium Ion id. Value '${uri.value}' must be a positive integer.`,
        );
      }
      await this.loadIon(version, ionId);
    } else {
      throw new Error(
        `Invalid content URI location. Location '${uri.location}' not supported.`,
      );
    }
  }

  private async loadIon(version: RenderableAsset, ionId: number) {
    const uri = version.contentUri;
    const layer = ImageryLayer.fromProviderAsync(
      IonImageryProvider.fromAssetId(ionId),
    );
    await this._rasterLayer.load(uri, layer);
  }

  private async loadMapTiler(version: RenderableAsset, mapTilerId: string) {
    const uri = version.contentUri;
    const maptilerKey = globalThis.__ENV?.RUNTIME_PUBLIC_MAPTILER_KEY;
    const assetId = encodeURIComponent(mapTilerId);

    const layer = new ImageryLayer(
      new UrlTemplateImageryProvider({
        url: `https://api.maptiler.com/maps/${assetId}/{z}/{x}/{y}.jpg?key=${maptilerKey}`,
      }),
    );
    await this._rasterLayer.load(uri, layer);
  }

  show(uri: string) {
    this._rasterLayer.show(uri);
  }

  hide(uri: string) {
    this._rasterLayer.hide(uri);
  }

  async flyTo(uri: string) {
    const layer = this._rasterLayer.get(uri);
    if (layer) await this._camera.flyTo(layer);
  }
}
